import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button.jsx'
import { Icon } from '@iconify/react'
import { motion } from 'framer-motion'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { initialChat } from '@/services/api.js'
import { AnalyticsService } from '@/services/analytics.js'
import { handleError } from '@/services/errorHandler'
import { useParams, useLocation } from 'react-router-dom'
import { useDashboardContext } from '@/context-state/DashboardContext'
import * as API from '@/services/api.js'

const CreativeAiChatContext = createContext()
let alreadyTriggeredAccountCreativeProfileCreation = false

// Helper function to extract preview text from AI response
const getPreviewText = (content, maxLength = 50) => {
    if (!content) return ''
    // Remove markdown and special characters for preview
    const cleanText = content.replace(/[*_#`~]/g, '').trim()
    if (cleanText.length <= maxLength) return cleanText
    return cleanText.substring(0, maxLength).trim() + '...'
}

const INITIAL_QUESTIONS_BANK = [
    "Give me ideas for new target personas",
    "What's the best way to optimize my creative performance?",
    "Give me ideas for new creatives",
    "Give me ideas to attract new audiences",
    "Give me ideas for new concepts",
    "What angles can I use to attract new audiences?",
]

export const CreativeAiChatProvider = ({ children }) => {
    const { user } = useAuthContext()
    const { spaceId } = useParams()
    const location = useLocation()
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [chatId, setChatId] = useState(null)
    const [messages, setMessages] = useState([])
    const [isFirstMessage, setIsFirstMessage] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [isTyping, setIsTyping] = useState(false)
    const [pendingQuestion, setPendingQuestion] = useState(null)
    const [creativeContext, setCreativeContext] = useState(null)
    // Brand-page asks: which advertiser the chat is about ({ theCoreAdvertiserId, name })
    const [advertiserContext, setAdvertiserContext] = useState(null)
    const [selectedInstructionId, setSelectedInstructionId] = useState(null)
    // Store chat status per adAccount: { [adAccountId]: 'idle' | 'preparing' | 'missing-context' | 'not-ready' }
    const [chatStatuses, setChatStatuses] = useState({})
    // Track checking status per adAccount
    const [isCheckingStatus, setIsCheckingStatus] = useState({})

    // AI settings — fetched once by ChatDataProvider, shared across sidebar + chat pages
    const [aiSettings, setAiSettings] = useState({ instructions: [], knowledge: [], hasBrandContext: true, isFetched: false })

    const { adAccountsContext } = useDashboardContext()
    const { chosenAdAccount } = adAccountsContext

    const prevMessagesLengthRef = useRef(0)
    const hasTriggeredAutoQuestionRef = useRef(false)
    const pollingIntervalRef = useRef(null)
    const isPollingRef = useRef(false)
    const isCheckingStatusRef = useRef(false)
    const notifyRef = useRef(null)
    const chosenAdAccountRef = useRef(chosenAdAccount)
    chosenAdAccountRef.current = chosenAdAccount

    const appendMessage = (message) => {
        setMessages(prev => [...prev, message])
    }

    const sendBackgroundQuestion = async (question, creativeId = null, creativeEntity = null, advertiserCtxOverride = null) => {
        // Capture the ad account at the time the request is made
        const requestAdAccount = chosenAdAccount

        try {
            AnalyticsService.track('creative-ai-chat-background-question-started', { question, creativeId })
            setIsLoading(true)

            // Always start a new chat for background questions as requested
            setMessages([])
            setChatId(null)
            setIsFirstMessage(true)

            if (creativeId || creativeEntity) {
                setCreativeContext({ creativeId, creativeEntity })
            }

            const userMessage = {
                id: Date.now(),
                role: 'user',
                content: question,
                timestamp: new Date()
            }
            // Use customSetMessages logic via setMessages to append
            setMessages([userMessage])

            const chatPayload = {
                prompt: question,
                adAccount: requestAdAccount,
                creativeId: creativeId
            }
            if (selectedInstructionId) {
                chatPayload.instructionId = selectedInstructionId
            }
            // Brand-page asks carry the advertiser so the AI knows which
            // brand the conversation is about without name resolution.
            // Callers (e.g. the brand page's floating bar) can pass it
            // directly; otherwise fall back to the drawer-set context.
            const effectiveAdvertiserCtx = advertiserCtxOverride ?? advertiserContext
            if (effectiveAdvertiserCtx) {
                chatPayload.advertiserContext = effectiveAdvertiserCtx
            }
            const response = await initialChat(chatPayload)

            // If the user switched ad account while waiting, discard the response
            if (requestAdAccount !== chosenAdAccountRef.current) {
                return
            }

            const { reply, chatId: newChatId, uiPayload } = response.data
            setChatId(newChatId)
            setIsFirstMessage(false)

            const aiMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: reply,
                uiPayload: uiPayload || null,
                timestamp: new Date()
            }
            setMessages(prev => [...prev, aiMessage])

            AnalyticsService.track('creative-ai-chat-background-question-success', { question })
        } catch (error) {
            handleError(error)
            toast.error('Failed to get AI response.')
        } finally {
            setIsLoading(false)
        }
    }

    const runAutomatedQuestion = async (question) => {
        // Capture the ad account at the time the request is made
        const requestAdAccount = chosenAdAccount

        try {
            AnalyticsService.track('creative-ai-chat-auto-question-started', { question })
            setIsLoading(true)
            const userMessage = {
                id: Date.now(),
                role: 'user',
                content: question,
                autoSuggested: true,
                timestamp: new Date()
            }
            appendMessage(userMessage)

            const chatPayload = { prompt: question, adAccount: requestAdAccount }
            if (selectedInstructionId) {
                chatPayload.instructionId = selectedInstructionId
            }
            const response = await initialChat(chatPayload)

            // If the user switched ad account while waiting, discard the response
            if (requestAdAccount !== chosenAdAccountRef.current) {
                return
            }

            const { reply, chatId: newChatId, uiPayload } = response.data

            setChatId(newChatId)
            setIsFirstMessage(false)

            const aiMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: reply,
                uiPayload: uiPayload || null,
                autoSuggested: true,
                timestamp: new Date()
            }

            appendMessage(aiMessage)

            AnalyticsService.track('creative-ai-chat-auto-question-success', {
                question,
                chatId: newChatId,
                hasUiPayload: !!uiPayload?.length
            })
        } catch (error) {
            handleError(error)
            AnalyticsService.track('creative-ai-chat-auto-question-failed', {
                question,
                error: error?.message
            })
            toast.error('Something went wrong while getting AI insights.')
        } finally {
            setIsLoading(false)
        }
    }

    // We trigger that in case we need to create a new account creative profile
    const createAccountCreativeProfileAsyncly = async () => {
        try {
            if (alreadyTriggeredAccountCreativeProfileCreation) {
                return
            }
            const now = new Date()
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
            const startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`
            const endDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
            await API.createBrainAccountCreativeProfile({ startDate, endDate, adAccount: chosenAdAccount })
            alreadyTriggeredAccountCreativeProfileCreation = true
        } catch (error) {
            handleError(error)
        }
    }

    const openDrawer = ({ question = null, creativeId = null, creativeEntity = null, advertiserContext: advertiserCtx = null } = {}) => {
        AnalyticsService.track('creative-ai-chat-drawer-opened', {
            questionProvided: !!question,
            creativeId,
            hasCreativeEntity: !!creativeEntity,
            hasAdvertiserContext: !!advertiserCtx
        })
        if (question) {
            // Every explicit ask starts a FRESH chat — clear whatever
            // conversation the drawer was holding before the question fires
            setMessages([])
            setChatId(null)
            setIsFirstMessage(true)
            setPendingQuestion(question)
        }
        if (creativeId || creativeEntity) {
            setCreativeContext({
                creativeId,
                creativeEntity
            })
        }
        // Always set (or clear) so a brand-scoped chat never leaks its
        // advertiser into an unrelated chat opened later
        setAdvertiserContext(advertiserCtx)
        setIsDrawerOpen(true)
    }

    const closeDrawer = () => {
        AnalyticsService.track('creative-ai-chat-drawer-closed')
        setIsDrawerOpen(false)
    }

    const toggleDrawer = () => {
        AnalyticsService.track('creative-ai-chat-drawer-toggled', { willOpen: !isDrawerOpen })
        setIsDrawerOpen(prev => !prev)
    }

    const clearChat = () => {
        AnalyticsService.track('creative-ai-chat-cleared')
        setMessages([])
        setChatId(null)
        setIsFirstMessage(true)
        setIsLoading(false)
        setIsTyping(false)
        setCreativeContext(null)
        setPendingQuestion(null)
        setSelectedInstructionId(null)
        prevMessagesLengthRef.current = 0
    }

    // Helper to create composite key for chat status storage
    const getStatusKey = (currentSpaceId, adAccountId) => `${currentSpaceId}_${adAccountId}`

    const getChatStatus = (adAccountId) => {
        if (!adAccountId) {
            return null
        }
        const key = getStatusKey(spaceId, adAccountId)
        return chatStatuses[key] || null
    }

    const setChatStatusForAccount = (adAccountId, status) => {
        if (!adAccountId) return
        const key = getStatusKey(spaceId, adAccountId)
        setChatStatuses(prev => ({
            ...prev,
            [key]: status
        }))
    }

    const clearChatStatus = (adAccountId) => {
        if (!adAccountId) {
            return
        }
        const key = getStatusKey(spaceId, adAccountId)
        setChatStatuses(prev => {
            const updated = { ...prev }
            delete updated[key]
            return updated
        })
    }

    // Fetch chat status from API
    const fetchChatStatus = async (adAccountId, currentSpaceId) => {
        if (!adAccountId || !currentSpaceId) {
            return null
        }

        // Prevent concurrent calls
        if (isCheckingStatusRef.current) {
            return null
        }

        const key = getStatusKey(currentSpaceId, adAccountId)

        try {
            isCheckingStatusRef.current = true
            setIsCheckingStatus(prev => ({ ...prev, [key]: true }))
            const { data } = await API.getChatStatus(adAccountId)
            // metadata.contexts['account-creative-profile']
            const missingAccountCreativeProfile = !data?.metadata?.contexts?.['account-creative-profile']
            if (missingAccountCreativeProfile) {
                createAccountCreativeProfileAsyncly()
            }
            const status = data?.status || 'not-ready'

            // Update status using the same key that was created at the start
            setChatStatuses(prev => ({
                ...prev,
                [key]: status
            }))

            setIsCheckingStatus(prev => ({ ...prev, [key]: false }))
            isCheckingStatusRef.current = false

            return status
        } catch (error) {
            console.error('Error checking chat status:', error)
            // Default to "not-ready" on error
            setChatStatuses(prev => ({
                ...prev,
                [key]: 'not-ready'
            }))
            setIsCheckingStatus(prev => ({ ...prev, [key]: false }))
            isCheckingStatusRef.current = false
            return 'not-ready'
        }
    }

    // Triggered from ChatPage when user opens chat with not-ready or missing-context status
    const triggerStatusCheck = async (adAccountId) => {
        if (!adAccountId) {
            return
        }
        return await fetchChatStatus(adAccountId, spaceId)
    }

    useEffect(() => {
        clearChat()
    }, [spaceId, chosenAdAccount])

    // Initial status fetch when chosenAdAccount or spaceId changes
    useEffect(() => {
        // if (!user?.allowChat) {
        //     return
        // }
        if (!chosenAdAccount || !spaceId) {
            // Clear polling if no ad account
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
                pollingIntervalRef.current = null
                isPollingRef.current = false
            }
            return
        }

        const key = getStatusKey(spaceId, chosenAdAccount)
        const currentStatus = chatStatuses[key]

        // Only fetch if we don't have a status yet OR if status is "idle" (keep it)
        if (currentStatus === 'idle') {
            // Already have idle status, don't re-fetch
            return
        }

        // If no status yet, fetch it
        if (!currentStatus) {
            // Here we fetch it for the first time.
            // Pass spaceId explicitly to ensure the key is consistent
            fetchChatStatus(chosenAdAccount, spaceId)
        }
    }, [chosenAdAccount, spaceId, chatStatuses]) // Re-fetch when space or ad account changes, include chatStatuses to react to updates

    // Re-check 'missing-context' on route change — user may have just configured their AI context in settings
    useEffect(() => {
        if (!chosenAdAccount || !spaceId) return
        const key = getStatusKey(spaceId, chosenAdAccount)
        const currentStatus = chatStatuses[key]
        if (currentStatus === 'missing-context') {
            setChatStatuses(prev => {
                const updated = { ...prev }
                delete updated[key]
                return updated
            })
        }
    }, [location.pathname])

    // Handle polling when status becomes "preparing"
    useEffect(() => {
        if (!chosenAdAccount || !spaceId) { //  || !user?.allowChat
            return
        }

        const key = getStatusKey(spaceId, chosenAdAccount)
        const currentStatus = chatStatuses[key]

        // Capture spaceId for use in the interval callback
        const capturedSpaceId = spaceId

        // If status is "preparing", start polling
        if (currentStatus === 'preparing' && !isPollingRef.current) {
            isPollingRef.current = true

            // Clear any existing interval
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
            }

            // Start polling every 8 seconds
            pollingIntervalRef.current = setInterval(async () => {
                const status = await fetchChatStatus(chosenAdAccount, capturedSpaceId)
                // Stop polling if status changes from "preparing"
                if (status !== 'preparing') {
                    isPollingRef.current = false
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current)
                        pollingIntervalRef.current = null
                    }
                    // Notify user that chat is ready
                    if (status === 'idle' || status === 'missing-context') {
                        notifyRef.current?.({
                            title: "Chat is ready",
                            description: "Your AI chat is now available",
                            type: "success"
                        })
                    }
                }
            }, 8000)
        } else if (currentStatus !== 'preparing') {
            // Clear interval when status is not "preparing"
            isPollingRef.current = false
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
                pollingIntervalRef.current = null
            }
        }

        // Cleanup on unmount or when status changes
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
                pollingIntervalRef.current = null
            }
            isPollingRef.current = false
        }
    }, [chosenAdAccount, spaceId, chatStatuses])

    // Cleanup polling on unmount
    useEffect(() => {
        // if (!user?.allowChat) {
        //     return
        // }
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
                pollingIntervalRef.current = null
            }
            isPollingRef.current = false
        }
    }, [])

    const notify = ({ title, description, type = 'info' }) => {
        AnalyticsService.track('creative-ai-chat-notification', {
            type,
            hasTitle: !!title,
            descriptionLength: description?.length || 0
        })
        const toastContent = (
            <div className="flex flex-col gap-3 w-full">
                <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/50 to-transparent z-10"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.2, delay: 0.2, ease: 'easeInOut' }}
                />


                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <motion.div
                        className="absolute w-32 h-32 rounded-full bg-gradient-to-br from-blue-200/60 to-purple-200/60 blur-xl"
                        animate={{
                            x: [0, 40, 0],
                            y: [0, -20, 0],
                            scale: [1, 1.2, 1],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        style={{ top: '10%', left: '10%' }}
                    />
                    <motion.div
                        className="absolute w-24 h-24 rounded-full bg-gradient-to-br from-pink-200/60 to-orange-200/60 blur-xl"
                        animate={{
                            x: [0, -30, 0],
                            y: [0, 30, 0],
                            scale: [1, 1.3, 1],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 0.8
                        }}
                        style={{ top: '60%', right: '10%' }}
                    />
                    <motion.div
                        className="absolute w-20 h-20 rounded-full bg-gradient-to-br from-purple-200/60 to-blue-200/60 blur-xl"
                        animate={{
                            x: [0, 20, 0],
                            y: [0, -20, 0],
                            scale: [1, 1.1, 1],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 1.6
                        }}
                        style={{ bottom: '15%', left: '30%' }}
                    />
                </div>


                <div className="flex-1 z-20">
                    {title && <div className="font-semibold text-sm mb-1">{title}</div>}
                    {description && <div className="text-sm text-muted-foreground">{description}</div>}
                </div>
                <div className="flex gap-2 z-20">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            toast.dismiss()
                        }}
                        className="flex-1 text-xs h-8"
                    >
                        Close
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => {
                            openDrawer()
                            toast.dismiss()
                        }}
                        className="flex-1 flex items-center justify-center gap-1 text-xs h-8"
                    >
                        <span>View more</span>
                        <Icon icon="ph:arrow-right" className="h-3 w-3" />
                    </Button>
                </div>
            </div>
        )

        // Custom styling for AI chat notifications
        const toastOptions = {
            duration: 20000,
            position: 'top-right',
            className: 'bg-background/20 backdrop-blur-xl relative overflow-hidden',
        }

        // Use the appropriate toast type
        switch (type) {
            case 'success':
                toast.success(toastContent, toastOptions)
                break
            case 'error':
                toast.error(toastContent, toastOptions)
                break
            case 'warning':
                toast.warning(toastContent, toastOptions)
                break
            default:
                toast(toastContent, toastOptions)
        }
    }

    // Keep notifyRef updated for use in polling
    notifyRef.current = notify

    const customSetMessages = (updater) => {
        setMessages(prevMessages => {
            const updatedMessages = typeof updater === 'function' ? updater(prevMessages) : updater

            if (updatedMessages.length < 2) {
                return updatedMessages // No previous messages to check against
            }

            // Get all payload keys from messages before the last one
            const previousPayloadKeys = new Set()
            for (let i = 0; i < updatedMessages.length - 1; i++) {
                const msg = updatedMessages[i]
                if (msg.uiPayload && msg.uiPayload.length > 0) {
                    const ids = msg.uiPayload
                        .map(item => item.externalId)
                        .filter(Boolean) // Remove null/undefined IDs

                    if (ids.length > 0) {
                        const key = ids.sort().join(',')
                        previousPayloadKeys.add(key)
                    }
                }
            }

            // Now, check the last message against the collected keys
            const lastMessage = updatedMessages[updatedMessages.length - 1]
            if (lastMessage.uiPayload && lastMessage.uiPayload.length > 0) {
                const currentIds = lastMessage.uiPayload
                    .map(item => item.externalId)
                    .filter(Boolean)

                if (currentIds.length > 0) {
                    const currentKey = currentIds.sort().join(',')
                    if (previousPayloadKeys.has(currentKey)) {
                        // This is a duplicate payload. Return a new array with the payload removed from the last message.
                        const messagesWithoutDuplicate = [...updatedMessages]
                        messagesWithoutDuplicate[messagesWithoutDuplicate.length - 1] = {
                            ...lastMessage,
                            uiPayload: null
                        }
                        return messagesWithoutDuplicate
                    }
                }
            }

            // No duplicate found, return the messages as is
            return updatedMessages
        })
    }

    // Monitor messages and send notification when AI responds while drawer is closed
    useEffect(() => {
        // if (!user?.allowChat) {
        //     return
        // }
        // Check if a new message was added
        // if (messages.length > prevMessagesLengthRef.current) {
        //     const lastMessage = messages[messages.length - 1]


        //     // Only notify if:
        //     // 1. The drawer is closed
        //     // 2. The last message is from the assistant
        //     if (!isDrawerOpen && lastMessage?.role === 'assistant' && lastMessage?.content) {
        //         const previewText = getPreviewText(lastMessage.content, 60)
        //         notify({
        //             title: "AI Response Ready",
        //             description: previewText,
        //             type: "info"
        //         })
        //     }
        // }

        // Update ref for next comparison
        prevMessagesLengthRef.current = messages.length
    }, [messages, isDrawerOpen])

    // useEffect(() => {
    // if (!user?.allowChat || hasTriggeredAutoQuestionRef.current) {
    //     return
    // }

    // const randomQuestion = INITIAL_QUESTIONS_BANK[Math.floor(Math.random() * INITIAL_QUESTIONS_BANK.length)]
    // if (!randomQuestion) {
    //     return
    // }

    // hasTriggeredAutoQuestionRef.current = true
    // AnalyticsService.track('creative-ai-chat-auto-question-triggered', { question: randomQuestion })
    // runAutomatedQuestion(randomQuestion)
    // }, [user?.allowChat])

    return (
        <CreativeAiChatContext.Provider
            value={{
                isDrawerOpen,
                openDrawer,
                closeDrawer,
                toggleDrawer,
                notify,
                // Chat state
                chatId,
                setChatId,
                messages,
                setMessages: customSetMessages,
                isFirstMessage,
                setIsFirstMessage,
                isLoading,
                setIsLoading,
                isTyping,
                setIsTyping,
                clearChat,
                // Creative context
                pendingQuestion,
                setPendingQuestion,
                creativeContext,
                setCreativeContext,
                // Advertiser (brand-page) context
                advertiserContext,
                setAdvertiserContext,
                // Instruction selection
                selectedInstructionId,
                setSelectedInstructionId,
                // Chat status
                getChatStatus,
                setChatStatus: setChatStatusForAccount,
                clearChatStatus,
                triggerStatusCheck,
                isCheckingStatus: isCheckingStatus[getStatusKey(spaceId, chosenAdAccount)] || false,
                sendBackgroundQuestion,
                // AI settings
                aiSettings,
                setAiSettings
            }}
        >
            {children}
        </CreativeAiChatContext.Provider>
    )
}

export const useCreativeAiChat = () => {
    const context = useContext(CreativeAiChatContext)
    if (!context) {
        throw new Error('useCreativeAiChat must be used within a CreativeAiChatProvider')
    }
    return context
}
